import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  useComplianceIncident,
  useStartInvestigation,
} from '@/hooks/use-compliance-incidents';
import { useOrgToday } from '@/hooks/use-settings';
import type { InvestigationCorrectiveAction, StartInvestigationPayload } from '@/lib/types';

export function InvestigatePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const incidentId = Number(id);
  const { data: incident, isLoading } = useComplianceIncident(incidentId);
  const startInvestigation = useStartInvestigation();
  const orgToday = useOrgToday();

  const [form, setForm] = useState<StartInvestigationPayload>({
    investigation_date: orgToday,
    root_cause: '',
    findings: '',
    recommendations: '',
  });

  const [actions, setActions] = useState<InvestigationCorrectiveAction[]>([]);

  const updateField = <K extends keyof StartInvestigationPayload>(
    key: K,
    value: StartInvestigationPayload[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const addAction = () => {
    setActions((prev) => [
      ...prev,
      { action: '', assignee: '', due_date: '', status: 'pending' },
    ]);
  };

  const updateAction = (index: number, field: keyof InvestigationCorrectiveAction, value: string) => {
    setActions((prev) =>
      prev.map((a, i) => (i === index ? { ...a, [field]: value } : a)),
    );
  };

  const removeAction = (index: number) => {
    setActions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    const payload: StartInvestigationPayload & { incidentId: number } = {
      ...form,
      corrective_actions: actions.length > 0 ? actions : undefined,
      incidentId,
    };

    startInvestigation.mutate(payload, {
      onSuccess: () => {
        navigate(`/compliance/incidents/${incidentId}`);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <h3 className="text-lg font-semibold">Incident not found</h3>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate('/compliance/incidents')}
        >
          Back to Register
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/compliance/incidents/${incidentId}`)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Incident
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Investigation</h1>
          <p className="text-muted-foreground">
            {incident.incident_number}: {incident.title}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Investigation Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="investigation_date">Investigation Date *</Label>
            <Input
              id="investigation_date"
              type="date"
              value={form.investigation_date}
              onChange={(e) => updateField('investigation_date', e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="root_cause">Root Cause *</Label>
            <Textarea
              id="root_cause"
              value={form.root_cause}
              onChange={(e) => updateField('root_cause', e.target.value)}
              placeholder="Identify the root cause of the incident"
              rows={4}
              required
            />
          </div>

          <div>
            <Label htmlFor="contributing_factors">Contributing Factors</Label>
            <Textarea
              id="contributing_factors"
              value={form.contributing_factors || ''}
              onChange={(e) => updateField('contributing_factors', e.target.value || undefined)}
              placeholder="Any contributing factors"
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="findings">Findings *</Label>
            <Textarea
              id="findings"
              value={form.findings}
              onChange={(e) => updateField('findings', e.target.value)}
              placeholder="Key findings from the investigation"
              rows={4}
              required
            />
          </div>

          <div>
            <Label htmlFor="recommendations">Recommendations *</Label>
            <Textarea
              id="recommendations"
              value={form.recommendations}
              onChange={(e) => updateField('recommendations', e.target.value)}
              placeholder="Recommended actions to prevent recurrence"
              rows={4}
              required
            />
          </div>
        </CardContent>
      </Card>

      {/* Corrective Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Corrective Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {actions.map((action, index) => (
            <div key={index} className="flex items-start gap-4 rounded-md border p-4">
              <div className="flex-1 space-y-3">
                <div>
                  <Label>Action</Label>
                  <Input
                    value={action.action}
                    onChange={(e) => updateAction(index, 'action', e.target.value)}
                    placeholder="Describe the corrective action"
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label>Assignee</Label>
                    <Input
                      value={action.assignee}
                      onChange={(e) => updateAction(index, 'assignee', e.target.value)}
                      placeholder="Who is responsible"
                    />
                  </div>
                  <div>
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={action.due_date}
                      onChange={(e) => updateAction(index, 'due_date', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeAction(index)}
                className="mt-6 text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}

          <Button variant="outline" onClick={addAction}>
            <Plus className="mr-2 h-4 w-4" />
            Add Corrective Action
          </Button>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => navigate(`/compliance/incidents/${incidentId}`)}
        >
          Cancel
        </Button>
        <Button
          data-validate-required="true"
          onClick={handleSubmit}
          disabled={startInvestigation.isPending}
        >
          {startInvestigation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Start Investigation
        </Button>
      </div>
    </div>
  );
}
